import './scss/App.scss';

import axios from "axios";
import { useState, useEffect, Fragment } from "react";
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';

import { HelmetProvider } from 'react-helmet-async';

// Home page
import Header from './components/03-organisms/header/Header.js';
import Products from './components/03-organisms/products/Products.js';
import Instagram from './components/03-organisms/instagram/Instagram.js';
import Testimonials from './components/03-organisms/testimonials/Testimonials.js';
// import Blogs from './components/Blogs.js';
import Footer from './components/03-organisms/footer/Footer.js';

// Secondary pages
import Navbar from './components/02-molecules/navbar/Navbar.js';
import ProductCategory from './components/04-templates/productCategory/ProductCategory.js';
import ProductDetails from './components/04-templates/productDetails/ProductDetails.js';
import Contact from './components/04-templates/contactPage/Contact.js';

// All pages
import ScrollTop from './components/02-molecules/scrollTop/ScrollTop.js';
import Error from './components/04-templates/errorPage/Error.js';
import TestimonialsPage from './components/04-templates/testimonialsPage/TestimonialsPage.js';
import MoreFurniture from './components/04-templates/moreFurniture/MoreFurniture.js';
import WhatsappContact from './components/02-molecules/whatsappContact/WhatsappContact.js';
import FAQs from './components/04-templates/FAQ/FAQs.js';
import AboutUs from './components/04-templates/aboutUs/AboutUs.js';

function App() {
  // Contentful API credentials
  const API_KEY = process.env.REACT_APP_API_KEY;
  const API_SPACEID = `aoml7bafjiv0`;

  // API URL for fetching data from Contentful
  const API_URL = `https://cdn.contentful.com/spaces/${API_SPACEID}/environments/master/entries?access_token=${API_KEY}`

  // State to store fetched data and loading status
  const [dataArray, setDataArray] = useState([]);
  const [assetsArray, setAssetsArray] = useState([]);
  const [loading, setLoading] = useState(true);

  // Effect hook for fetching data from Contentful API
  useEffect(() => {
    axios({
      method: 'GET',
      url: API_URL,
      params: {
        // Include linked fields
        include: 2, // You can adjust the level based on your nesting depth
      },
    }).then(response => {
      setDataArray(response.data.items);
      setAssetsArray(response.data.includes.Asset);
      setLoading(false);
    }).catch(error => {
      console.error('Error fetching data from Contentful:', error);
      setLoading(false);
      // alert('No data received. Please try again later!');
    });
  }, [API_URL]);

  // Filtering data to get an array of product category
  const productCategoryArray = dataArray.filter((data) => data.fields.contentType.toLowerCase() === 'product category');

  // Filtering data to get an array of product icons
  const productIcons = assetsArray.filter((asset) => asset.fields.description === 'icon');

  // Filtering and mapping data to get an array of products with modified structure
  const productsArray = dataArray
    .filter((data) => data.fields.contentType === 'Products')
    .map((data) => ({ fields: data.fields, id: data.sys.id, metadata: data.metadata }));

  // Mapping assets data to a modified structure
  const assetsData = assetsArray.map((asset) => ({
    fields: asset.fields,
    id: asset.sys.id,
    metadata: asset.metadata,
  }));

  const productAssetArray = productsArray.map((product) => {
    const images = assetsData.filter((asset) => product.fields.productName.toLowerCase() === asset.fields.title.toLowerCase());

    // Find the image with the tag, if any
    const mainImage = images.find(image => image.metadata.tags.length && image.metadata.tags[0].sys.id === 'mainImage');

    // Move the main image to the front
    if (mainImage) {
      // Remove the main image from its original position
      images.splice(images.indexOf(mainImage), 1);
      // Add it to the front
      images.unshift(mainImage);
    }

    return {
        product,
        images
    };
  })

  // useLocation hook
  const location = useLocation();

  // New useEffect hook for scrolling to top when pathname changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // If data is still loading, display a loading message
  if (loading) {
    return <div className='loading-dots'>
      Loading
      <span></span>
      <span></span>
      <span></span>
    </div>
  }

  // handle loading state for images
    const handleLoading = (event) => {
        // setting display property for loading container
        const loadingEl = event.target.parentElement.children[0];
        loadingEl.style.display = "none";
    }

  // Rendering the components with processed data
  return (
    <HelmetProvider>
    <div className={`main-container ${location.pathname !== '/' ? 'main-container__secondary' : ''}`}>
      
      {/* Conditionally render Navbar only on non-home pages */}
      {location.pathname !== '/' && (
        <header className='secondary-header'>
          <div className='wrapper'>
            <Navbar productCategoryArray={productCategoryArray} />
          </div>
        </header>
      )}
      
      <Routes>    
        {/* Home Page Route */}
        <Route path='/' element={
          <Fragment>
            {/* Header component on the home page */}
            <Header />

            <div className="wrapper">
              {/* Products section on the home page */}
              <Products productIcons={productIcons} productCategoryArray={productCategoryArray} handleLoading={handleLoading} />

              <Instagram  dataArray={dataArray} />

              {/* Testimonials on home page*/}
              <Testimonials dataArray={dataArray} assetsArray={assetsArray} handleLoading={handleLoading} />

              {/* <Blogs dataArray={dataArray} /> */}
            </div>
          </Fragment>
        }/>

        {/* Product Category Route */}
        <Route path="/:categoryTitle" exact element={
          <ProductCategory
            productCategoryArray={productCategoryArray}
            productAssetArray={productAssetArray}
            handleLoading={handleLoading}
          />
          }
        />

        {/* Product Details Route */}
        <Route path="/:categoryTitle/:productName" exact element={
          <ProductDetails productAssetArray={productAssetArray} handleLoading={handleLoading}/>
          }
        />

        {/* Route for 404 Error Page */}
        <Route path="/404" element={<Error />} />
        <Route path="*" element={<Navigate to="/404" />} />

        {/* Route for Contact page */}
        <Route path="/contact" element={<Contact />} />

        {/* Route for FAQ page */}
        <Route path="/FAQs" element={<FAQs dataArray={dataArray} />} />

        {/* Route for FAQ page */}
        <Route path="/about-us" element={<AboutUs />} />

        {/* Route for Testimonials page */}
        <Route path="/testimonials" element={<TestimonialsPage dataArray={dataArray} assetsArray={assetsArray} handleLoading={handleLoading}/>} />

        {/* Route for More Furniture page */}
        <Route path="/more-furniture" element={<MoreFurniture productIcons={productIcons} productCategoryArray={productCategoryArray} handleLoading={handleLoading}/>} />

        {/* Product Category Route from More Furniture Page */}
        <Route path="/more-furniture/:categoryTitle" exact element={
          <ProductCategory
            productCategoryArray={productCategoryArray}
            productAssetArray={productAssetArray}
            handleLoading={handleLoading}
          />
          }
        />

        {/* Product Details Route from More Furniture Page */}
        <Route path="/more-furniture/:categoryTitle/:productName" exact element={
          <ProductDetails productAssetArray={productAssetArray} handleLoading={handleLoading}/>
          }
        />
      </Routes>
      
      <WhatsappContact />
      <ScrollTop />
      <Footer />
    </div>
    </HelmetProvider>
  );
}

export default App;
